import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import useAuth from '../../auth/useAuth';
import { useSoundStatus } from '../../context/SoundStatusContext';
import { useTour } from '../../context/TourContext';

import { calculateTimeInSeconds, parseDescription } from '../../utils';
import {
  pixelsPerFall,
  blockHeight,
  blockMaxWidth,
  fallingAreaBottomGap
} from '../../vars';

import Intro from '../UI/game/intro';
import Header from '../UI/game/header';
import Modal from '../UI/modal';
import FallingBlock from '../UI/game/falling-block';
import Column from '../UI/game/column';
import FooterMessage from '../UI/game/footer-message';
import Hint from '../UI/hint';

import {
  generalInfoQuery,
  getDecisions,
  updateGameSession,
  postDecisionsBulk
} from './api';

import { gameQuestionsLimit } from './data';

import {
  validateDecision,
  calculateScore,
  saveSessionToStorage,
  loadSessionFromStorage,
  clearSessionFromStorage,
  formatDecisionsForBulkPost
} from './validation';

import './styles.css';

import CountdownSound from './sounds/countdown.mp3';
import GameProcessSound from './sounds/game-process.mp3';
import ClickSound from './sounds/click.mp3';
import ItemHitSpikeSound from './sounds/item-hit-spike.mp3';
import AnswerCorrectSound from './sounds/answer-correct.mp3';
import AnswerWrongSound from './sounds/answer-wrong.mp3';

// Map of scenario-level name → reflection tour name. Levels listed here
// share the Balance Sheet / Income Statement two-column layout, so the
// Column components also receive matching `tourId` props for highlighting.
const REFLECTION_TOURS_BY_LEVEL = {
  'class-elements': 'class-elements-reflection',
  'class-default': 'class-default-reflection',
  'class-tricky': 'class-tricky-reflection',
};

export default function Classification({levelNumber, scenarioLevels, answerData}) {
  const countdownEffect = useRef(new Audio(CountdownSound));
  const gameProcessEffect = useRef(new Audio(GameProcessSound));
  const clickEffect = useRef(new Audio(ClickSound));
  const itemHitSpikeEffect = useRef(new Audio(ItemHitSpikeSound));
  const answerCorrectEffect = useRef(new Audio(AnswerCorrectSound));
  const answerWrongEffect = useRef(new Audio(AnswerWrongSound));

  const postInFlightRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();

  /* Prevent sound from playing on other pages */
  useEffect(() => {
    const currentGameProcessEffect = gameProcessEffect.current;

    return () => {
      if (currentGameProcessEffect) {
        currentGameProcessEffect.pause();
      }
    };
  }, [location]);

  const {
    getUser,
    getCurrentClassificationGameId
  } = useAuth();

  const user = getUser();
  const gameId = getCurrentClassificationGameId();

  const { soundStatus } = useSoundStatus();

  const { data: generalInfo } = useQuery(generalInfoQuery());
  const gamesSessions = generalInfo?.sessions;

  const [currentGameSessionId, setCurrentGameSessionId] = useState(0);

  useEffect(() => {
    if(gamesSessions && gameId && user) {
      const currentGameSession = gamesSessions.find(session => session.user === user?.id && session.game === gameId && !session.completed);
      
      if (currentGameSession) {
        setCurrentGameSessionId(currentGameSession?.id);
      } else {
        const completedGameSession = gamesSessions
          .filter(session => session.user === user?.id && session.game === gameId && session.completed)
          .reduce((maxSession, session) => (session.id > (maxSession?.id || 0) ? session : maxSession), null);
  
        setCurrentGameSessionId(completedGameSession?.id);
      }
    }
  }, [gamesSessions, gameId, user]);

  // Replay guard: if this level is already completed in the resolved
  // session (e.g. the player hit the back button after completing it),
  // skip past the gameplay component and land them on the points page.
  // Fires once per mount via a ref so it can't re-trigger after the
  // player completes this level mid-session.
  const replayGuardCheckedRef = useRef(false);
  useEffect(() => {
    if (replayGuardCheckedRef.current) return;
    if (!gamesSessions || !currentGameSessionId) return;
    const session = gamesSessions.find(s => s.id === currentGameSessionId);
    if (!session) return;
    replayGuardCheckedRef.current = true;
    if (session[`level${levelNumber}`] === 'completed') {
      navigate(`/classification/level/${levelNumber}/points/`, { replace: true });
    }
  }, [gamesSessions, currentGameSessionId, levelNumber, navigate]);

  useEffect(() => {
    if(!gameId) return;
    if (gamesSessions) {
      const currentGameSession = gamesSessions
        .filter(session => session.user === user?.id && session.is_ready === true && session.game === gameId)
        .reduce((maxSession, session) => (session.id > (maxSession?.id || 0) ? session : maxSession), null);
      if (!currentGameSession) {
        navigate('/classification/level/1/tutorial');
      } else {
        const firstIncompleteLevel = ['level1', 'level2', 'level3', 'level4', 'level5'].findIndex(level => currentGameSession[level] !== 'completed') + 1;

        if(currentGameSession.completed && currentGameSession[`level${levelNumber}`] === null && !isModalShown) {
          navigate('/classification/level/1/');
        } else {
          if(levelNumber > firstIncompleteLevel) {
            navigate(`/classification/level/${firstIncompleteLevel}/`);
          }
        }
      }
    }
  }, [gamesSessions, navigate, user?.id, gameId]);

  const fallingAreaRef = useRef(null);
  // Anchor for `game_time_elapsed` (cumulative seconds since game-start);
  // set once when isPlaying flips to true. Used to record a per-decision
  // running tally on each new decision so the bulk POST sends a value
  // that grows monotonically across rows instead of a constant level
  // total stamped on every row.
  const gameStartRef = useRef(null);

  const [questionTries, setQuestionTries] = useState({});
  const [allDecisions, setAllDecisions] = useState([]);

  const [scenarioLevel, setScenarioLevel] = useState({
    name: '',
    description: '',
    fallingSpeed: 1,
    enabled: false
  });

  const { startTour } = useTour();

  const [isPlaying, setIsPlaying] = useState(false);
  const [isLevelCompleted, setIsLevelCompleted] = useState(false);

  const [fallingBlocks, setFallingBlocks] = useState([]);
  const [availableBlockItems, setAvailableBlockItems] = useState([]);
  const [hoveredItem, setHoveredItem] = useState(0);
  const [prePlacedItems, setPrePlacedItems] = useState({});
  const availableBlockItemsRef = useRef(availableBlockItems);

  const games = generalInfo?.games;
  const currentGame = games?.find(g => g.id === gameId);
  const isOnboardingDemo = currentGame?.name === 'Demo Assignment';

  const [isGameBelongsToTournament, setIsGameBelongsToTournament] = useState(null);

  const [leftIntroTitle, setLeftIntroTitle] = useState(null);
  const [leftIntroSubtitle, setLeftIntroSubtitle] = useState(null);
  const [leftIntroDescription, setLeftIntroDescription] = useState(null);

  const [rightIntroTitle, setRightIntroTitle] = useState(null);
  const [rightIntroSubtitle, setRightIntroSubtitle] = useState(null);
  const [rightIntroDescription, setRightIntroDescription] = useState(null);

  const [isIntroContentCentered, setIsIntroContentCentered] = useState(false);
  const [isIntroSubtitleContentReversed, setIsIntroSubtitleContentReversed] = useState(false);
  useEffect(() => {
    if(gameId && scenarioLevel && games) {
      const currentGame = games.find(game => game.id === gameId);
      const isGameBelongsToTournament = currentGame?.tournament_ids?.length > 0;
      setIsGameBelongsToTournament(isGameBelongsToTournament);

      setLeftIntroTitle(`Level ${levelNumber}`);
      const { subtitle, description } = parseDescription(scenarioLevel.description);
      setLeftIntroSubtitle(subtitle);
      setRightIntroDescription(description);
    }
  }, [games, gameId, levelNumber, scenarioLevel]);

  const { data: decisionsResponse, isSuccess: isDecisionsSuccess } = useQuery({
    queryKey: ['decisionsResponse', { levelNumber, userId: user?.id, gameId, gameSessionId: currentGameSessionId }],
    queryFn: () => getDecisions({ levelNumber, userId: user?.id, gameId, gameSessionId: currentGameSessionId }),
    enabled: !!user?.id && levelNumber !== undefined && !!gameId && !!currentGameSessionId,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if(scenarioLevels.length) {
      const scenarioLevel = scenarioLevels.find(scenarioLevel => scenarioLevel.level === levelNumber);
      if(scenarioLevel) {
        setScenarioLevel({
          id: scenarioLevel.id,
          name: scenarioLevel.name,
          description: scenarioLevel.description,
          fallingSpeed: scenarioLevel.falling_speed * 1000,
          duration: calculateTimeInSeconds(scenarioLevel.duration || '00:03:00')
        });
      }
    }
  }, [levelNumber, scenarioLevels]);

  const [options, setOptions] = useState([]);

  // Pill is derived from `allDecisions` so it can never drift from the
  // canonical decision set. Each correct decision contributes its own
  // score_points exactly once (replace-or-insert by question id in
  // handleDrop and rehydration), and after a successful bulk POST the
  // decisions query is invalidated so this memo recomputes against
  // backend-recomputed values.
  const totalScore = useMemo(() => {
    const scoreByQuestion = new Map();
    for (const d of allDecisions) {
      if (d.is_correct) scoreByQuestion.set(d.question, d.score_points || 0);
    }
    let sum = 0;
    for (const points of scoreByQuestion.values()) sum += points;
    return sum;
  }, [allDecisions]);
  const [shouldScorePlaySound, setShouldScorePlaySound] = useState(false);

  const queryClient = useQueryClient();

  const updateGameSessionMutation = useMutation({
    mutationFn: (data) => updateGameSession(data),
    onSuccess: () => {
      // The level-routing effect reads firstIncompleteLevel from
      // gamesSessions — without this refetch the cache still says
      // levelN isn't completed and the user gets bounced back to the
      // level they just finished. The single-fire guard above
      // prevents this from cascading into duplicate mutates.
      queryClient.invalidateQueries({ queryKey: ['generalInfo'] });
    },
    onError() {
      console.log('Server error');
    }
  });

  useEffect(() => {
    const currentScenarioLevel = scenarioLevels.find(sl => sl.id === scenarioLevel.id);
    const itemOptions = currentScenarioLevel?.options || [];
    const itemQuestions = currentScenarioLevel?.questions || [];

    if(
      itemOptions.length &&
      itemQuestions.length &&
      isDecisionsSuccess
    ) {
      const decisions = decisionsResponse || [];

      // Build a lookup map from option account_item.id to the option object
      const allOptionsMap = new Map();
      itemOptions.forEach(opt => {
        allOptionsMap.set(opt.account_item.id, opt);
      });

      // Build a broader map from all scenario levels' options for parent fallback
      const allLevelsOptionsMap = new Map();
      scenarioLevels.forEach(sl => {
        (sl.options || []).forEach(opt => {
          if (!allLevelsOptionsMap.has(opt.account_item.id)) {
            allLevelsOptionsMap.set(opt.account_item.id, opt);
          }
        });
      });

      const createItemObject = (accountItem, optionData, children = []) => {
        const hierarchicalItem = {
          id: optionData && optionData.id,
          order: optionData && optionData.order,
          name: accountItem && accountItem.name,
          description: accountItem && accountItem.description,
          children: Array.isArray(children) ? children : [children],
          itemId: accountItem && accountItem.id
        };

        if (accountItem && accountItem.parents && accountItem.parents.length) {
          let parentItemId = accountItem.parents.find(parentId => allOptionsMap.has(parentId));

          if (parentItemId) {
            const parentOption = allOptionsMap.get(parentItemId);
            if (parentOption) {
              return createItemObject(parentOption.account_item, parentOption, [hierarchicalItem]);
            }
          }

          parentItemId = accountItem.parents.find(parentId => allLevelsOptionsMap.has(parentId));
          if (parentItemId) {
            const parentOption = allLevelsOptionsMap.get(parentItemId);
            if (parentOption) {
              return createItemObject(parentOption.account_item, parentOption, [hierarchicalItem]);
            }
          }

          return hierarchicalItem;
        } else {
          return hierarchicalItem;
        }
      }

      const hierarchicalOptions = itemOptions.map(option => {
        return createItemObject(option.account_item, option);
      });

      function mergeAndConcatenateDuplicates(items) {
        const uniqueItemsMap = new Map();

        items.forEach(item => {
          if (!uniqueItemsMap.has(item.id)) {
            uniqueItemsMap.set(item.id, { ...item, children: item.children ? [...item.children] : [] });
          } else {
            const existingItem = uniqueItemsMap.get(item.id);
            if (item.children && item.children.length > 0) {
              existingItem.children = existingItem.children.concat(item.children);
            }
          }
        });

        uniqueItemsMap.forEach((value, key) => {
          if (value.children && value.children.length > 0) {
            value.children = mergeAndConcatenateDuplicates(value.children);
          }
        });

        return Array.from(uniqueItemsMap.values());
      }

      function sortOptionsDataByOrder(data) {
        const sortedData = data.sort((a, b) => a.order - b.order);

        sortedData.forEach(item => {
          if (item.children && item.children.length > 0) {
            item.children = sortOptionsDataByOrder(item.children);
          }
        });

        return sortedData;
      }

      const optionsChildrenMerged = mergeAndConcatenateDuplicates(hierarchicalOptions);
      const sortedOptionsData = sortOptionsDataByOrder(optionsChildrenMerged);
      setOptions([...sortedOptionsData]);

      const questionsData = itemQuestions.map(itemQuestion => {
        return {
          id: itemQuestion.id,
          accountItemId: itemQuestion.account_item?.id,
          order: itemQuestion.order,
          name: itemQuestion.account_item?.name,
          description: itemQuestion.account_item?.description,
        }
      });

      // Hydrate any locally-saved progress for this session that hasn't
      // been POSTed yet. Only use it if it matches the current game/level
      // (the same game_session can span multiple levels).
      const savedSession = loadSessionFromStorage(currentGameSessionId);
      const isLocalForThisLevel =
        savedSession?.gameId === gameId &&
        savedSession?.levelNumber === levelNumber;

      // Marker check: if we already successfully bulk-POSTed for this
      // session+level, the backend is canonical. Treat localStorage as
      // empty regardless of what it actually contains — and lazily GC
      // the stale entry so it doesn't keep accumulating.
      const postCompletedKey = `postCompleted-${currentGameSessionId}-level${levelNumber}`;
      const wasPosted = sessionStorage.getItem(postCompletedKey) === 'true';
      if (wasPosted && isLocalForThisLevel) {
        clearSessionFromStorage(currentGameSessionId);
      }

      const localDecisions      = (wasPosted || !isLocalForThisLevel) ? []   : (savedSession.allDecisions  || []);
      const localQuestionTries  = (wasPosted || !isLocalForThisLevel) ? {}   : (savedSession.questionTries || {});

      const apiCorrectDecisions = decisions.filter(decision => decision.is_correct === true);
      const localCorrectDecisions = localDecisions.filter(decision => decision.is_correct === true);
      const correctDecisions = [...apiCorrectDecisions, ...localCorrectDecisions];

      const questionIdsToRemove = new Set(correctDecisions.flatMap(decision => decision.question));
      const [questionsToFall, questionsToBePlaced] = questionsData.reduce((result, question) => {
        if (questionIdsToRemove.has(question.id)) {
          result[1].push(question);
        } else {
          result[0].push(question);
        }
        return result;
      }, [[], []]);

      // Build a map from option ID to the option in the sorted hierarchy
      const findOptionInHierarchy = (optionId, options) => {
        for (const option of options) {
          if (option.id === optionId) return option.id;
          if (option.children?.length) {
            const found = findOptionInHierarchy(optionId, option.children);
            if (found) return found;
          }
        }
        return null;
      };

      // Use decision.selected_option to determine placement
      const prePlacedItemsByDropArea = questionsToBePlaced.reduce((acc, item) => {
        const correctDecision = correctDecisions.find(d => d.question === item.id);
        if (correctDecision) {
          const dropAreaId = findOptionInHierarchy(correctDecision.selected_option, sortedOptionsData);
          if (dropAreaId) {
            acc[dropAreaId] = acc[dropAreaId] || [];
            acc[dropAreaId].push(item);
          }
        }
        return acc;
      }, {});
      setPrePlacedItems({...prePlacedItemsByDropArea});

      // const allQuestionsHaveOrder = questionsData.every(item => item.hasOwnProperty('order'));
      let sortedOrRandomQuestionsData = [];
      if(questionsToBePlaced.length < gameQuestionsLimit) {
        const apiWrongDecisions = decisions.filter(decision => decision.is_correct === false);
        const localWrongDecisions = localDecisions.filter(decision => decision.is_correct === false);
        const wrongDecisions = [...apiWrongDecisions, ...localWrongDecisions];
        const wrongDecisionsIdsToAdd = new Set(
          wrongDecisions.flatMap(decision => decision.question).filter(id => !questionIdsToRemove.has(id))
        );

        const questionsWithWrongDecisions = questionsToFall.filter(question => wrongDecisionsIdsToAdd.has(question.id));
        const questionsNeeded = gameQuestionsLimit - questionsToBePlaced.length - questionsWithWrongDecisions.length;
        const remainingQuestions = questionsToFall.filter(question => !wrongDecisionsIdsToAdd.has(question.id));

        const shuffled = remainingQuestions.sort(() => 0.5 - Math.random());
        const randomQuestions = shuffled.slice(0, Math.max(0, questionsNeeded));

        sortedOrRandomQuestionsData = [...questionsWithWrongDecisions, ...randomQuestions];
      }

      setAvailableBlockItems(sortedOrRandomQuestionsData);
      if(!sortedOrRandomQuestionsData.length && correctDecisions.length) {
        setIsPlaying(true);
        setIsLevelCompleted(true);
      }

      // Build allDecisions from API ∪ local, preserving every attempt
      // (correct and incorrect) so the bulk POST and any subsequent
      // refetch carry the full retry history. Dedup is by (question,
      // timestamp) only, which is unique per drop — so the same row
      // appearing in both API and local (after a successful POST)
      // collapses to one, while different attempts on the same
      // question stay separate.
      const seen = new Set();
      const merged = [];
      for (const d of [...decisions, ...localDecisions]) {
        const key = `${d.question}|${d.timestamp}`;
        if (d.timestamp && seen.has(key)) continue;
        if (d.timestamp) seen.add(key);
        merged.push(d);
      }
      setAllDecisions(merged);
      setQuestionTries(localQuestionTries);

      // Sum elapsed time across both sources for the level timer. API
      // decisions store time_elapsed as "HH:MM:SS"; local decisions store
      // it as integer seconds. Time isn't double-recorded per question
      // because correct decisions don't get re-dropped.
      const getDecisionSeconds = (decision) => {
        if (decision.time_elapsed == null) return 0;
        return typeof decision.time_elapsed === 'number'
          ? decision.time_elapsed
          : calculateTimeInSeconds(decision.time_elapsed);
      };

      const apiElapsedTime = apiCorrectDecisions.reduce(
        (total, decision) => total + getDecisionSeconds(decision),
        0
      );

      const localElapsedTime = localDecisions.reduce(
        (total, decision) => total + getDecisionSeconds(decision),
        0
      );

      setTime(apiElapsedTime + localElapsedTime);
    }
  }, [scenarioLevels, scenarioLevel.id, decisionsResponse, levelNumber, user?.id, isDecisionsSuccess, currentGameSessionId, gameId]);

  const [isFooterMessageShown, setIsFooterMessageShown] = useState(false);
  const [footerMessageType, setFooterMessageType] = useState();
  const [footerMessageDescription, setFooterMessageDescription] = useState();

  const [isHintShown, setIsHintShown] = useState(false);
  const [hintContent, setHintContent] = useState(<span></span>);

  const handleMouseEnter = (id, hoverContent) => {
    setHoveredItem(id);

    setHintContent(hoverContent);
    setIsHintShown(true);
  }

  const handleMouseLeave = () => {
    setHoveredItem(0);
    setIsHintShown(false);
  }
  
  const handleMouseDown = () => {
    setHoveredItem(0);
    setIsHintShown(false);
  }

  const showFooterMessage = (type, description) => {
    setFooterMessageType(type);
    setFooterMessageDescription(description);
    setIsFooterMessageShown(true);
  }

  const [isModalShown, setIsModalShown] = useState(false);
  const [isModalOverlayVisible, setIsModalOverlayVisible] = useState(true);
  const [isModalSmall, setIsModalSmall] = useState(false);
  const [hasShownIntroModal, setHasShownIntroModal] = useState(false);

  const [modalContent, setModalContent] = useState(
    <div className="modal-center vertical">
      <div><span>Game start!</span></div>
      <div><span className='modal-counter'>3</span></div>
    </div>
  );

  const [time, setTime] = useState(0);
  const timeRef = useRef(time);
  useEffect(() => {
    timeRef.current = time;
  }, [time]);

  useEffect(() => {
    if (!isPlaying) return;
    const timerInterval = setInterval(() => {
      const newTime = timeRef.current + 1;
      setTime(newTime);
      /* uncomment this one when we will work with durations limitation from the server */
      // if (newTime >= scenarioLevel.duration) {
      //   setIsPlaying(false);
      //   setModalContent(<div className="modal-center">Time is up</div>);
      //   setIsModalShown(true);
      //   clearInterval(timerInterval);
      // }
    }, 1000);

    return () => clearInterval(timerInterval);
  }, [isPlaying, scenarioLevel]);

  const [fallingAreaWidth, setFallingAreaWidth] = useState(0);
  const [fallingAreaHeight, setFallingAreaHeight] = useState(0);

  useEffect(() => {
    const currentFallingArea = fallingAreaRef.current;
  
    const updateFallingAreaSizes = () => {
      const width = currentFallingArea?.clientWidth || 0;
      const height = currentFallingArea?.clientHeight || 0;
      setFallingAreaWidth(width);
      setFallingAreaHeight(height);
    };
  
    updateFallingAreaSizes();
    const resizeObserver = new ResizeObserver(entries => {
      updateFallingAreaSizes();
    });
  
    if (currentFallingArea) {
      resizeObserver.observe(currentFallingArea);
    }
  
    return () => {
      if (currentFallingArea) {
        resizeObserver.unobserve(currentFallingArea);
      }
    };
  }, []);
  
  useEffect(() => {
    availableBlockItemsRef.current = availableBlockItems;
  }, [availableBlockItems]);

  // const allQuestionsHaveOrderRef = useRef();

  useEffect(() => {
    if (!isPlaying) return;
  
    const generateBlock = () => {
      if (availableBlockItemsRef.current.length === 0) {
        return;
      }
  
      let blockToGenerate;
      let blockIndex;
  
      blockIndex = Math.floor(Math.random() * availableBlockItemsRef.current.length);
      blockToGenerate = availableBlockItemsRef.current[blockIndex];
      
      const isLeftSide = Math.random() < 0.5;
      let left = 'auto';
      let right = 'auto';
  
      if (isLeftSide) {
        left = Math.random() * (fallingAreaWidth / 2 - 25) + 5;
      } else {
        right = Math.random() * (fallingAreaWidth / 2 - 25) + 5;
      }
  
      const newBlock = {
        id: blockToGenerate.id,
        accountItemId: blockToGenerate.accountItemId,
        name: blockToGenerate.name,
        order: blockToGenerate.order,
        parents: blockToGenerate.parents,
        description: blockToGenerate.description,
        startTime: blockToGenerate.startTime || Date.now(),
        accuracyCoefficient: blockToGenerate.accuracyCoefficient,
        left: left,
        right: right,
        y: 0,
        width: blockMaxWidth,
      };
  
      setFallingBlocks(prevBlocks => [...prevBlocks, newBlock]);
      setAvailableBlockItems(prev => prev.filter((_, index) => index !== blockIndex));
    };
  
    // Immediately generate a block if fallingBlocks is empty
    if (fallingBlocks.length === 0) {
      setTimeout(generateBlock, 1000);
    }
  
    const generateBlockInterval = setInterval(generateBlock, scenarioLevel.fallingSpeed * 3);
  
    return () => {
      clearInterval(generateBlockInterval);
    };
  }, [isPlaying, fallingAreaWidth, scenarioLevel.fallingSpeed, fallingBlocks.length, availableBlockItemsRef]);
  
  const fallBlocks = () => {
    const bottomGap = blockHeight + fallingAreaBottomGap + pixelsPerFall;

    setFallingBlocks((prevBlocks) => {
      const updatedBlocks = prevBlocks.map(block => {
        const blockY = hoveredItem === block.id ? block.y : block.y + pixelsPerFall;
        return({
          ...block,
          y: blockY,
        })
      });

      const blocksToRemove = updatedBlocks.filter(block => block.y > fallingAreaHeight - bottomGap);

      if (blocksToRemove.length > 0) {
        if (soundStatus === 'enabled') {
          itemHitSpikeEffect.current.play().catch(e => console.log('Error playing item hit spike sound:', e));
        }
        
        const newAvailableBlocks = blocksToRemove.map(block => ({
          id: block.id,
          name: block.name,
          order: block.order,
          parents: block.parents,
          description: block.description,
          startTime: block.startTime,
          accuracyCoefficient: block.accuracyCoefficient
        }));

        setAvailableBlockItems(prevItems => {
          const filteredNewBlocks = newAvailableBlocks.filter(newBlock => 
            !prevItems.some(item => item.id === newBlock.id)
          );

          return [...prevItems, ...filteredNewBlocks];
        });

        return updatedBlocks.filter(block => !blocksToRemove.includes(block));
      }

      return updatedBlocks;
    });

  };

  useEffect(() => {
    if (!isPlaying) return;
    // Stamp gameStartRef the first time isPlaying flips on (countdown
    // ends or rehydration sets it for an already-completed level), so
    // every decision can compute its cumulative `game_time_elapsed`.
    if (gameStartRef.current == null) gameStartRef.current = Date.now();

    const fallBlocksInterval = setInterval(fallBlocks, scenarioLevel.fallingSpeed);

    return () => {
      clearInterval(fallBlocksInterval);
    };
  }, [isPlaying, fallingAreaHeight, scenarioLevel.fallingSpeed, hoveredItem]);

  const handleDrop = (item, column, callback) => {
    setFallingBlocks(prevBlocks => prevBlocks.filter(block => block.id !== item.id));

    const isCorrect = validateDecision(answerData, item.accountItemId, column.itemId);

    // Diagnostic: whenever a drop scores WRONG, log the exact inputs
    // that validateDecision saw. Silent on correct drops so console
    // stays clean. Compare `parentIdsAtDropTime` against what the
    // items-view endpoint returns for the same item_id — a mismatch
    // pinpoints a stale-closure or race issue.
    if (!isCorrect) {
      const matched = answerData?.find(a => a.item_id === item.accountItemId);
      // eslint-disable-next-line no-console
      console.log('[dropDiag/classification] WRONG verdict', {
        itemName: item.name,
        itemAccountItemId: item.accountItemId,
        columnName: column.name,
        columnItemId: column.itemId,
        answerDataLength: answerData?.length,
        matchedAnswerItemId: matched?.item_id,
        parentIdsAtDropTime: matched?.parent_ids,
        includesResult: matched?.parent_ids?.includes(column.itemId),
      });
    }

    const currentTries = (questionTries[item.id] || 0) + 1;
    setQuestionTries(prev => ({ ...prev, [item.id]: currentTries }));

    const nowMs = Date.now();
    const timeElapsedSeconds = Math.round((nowMs - item.startTime) / 1000) || 1;
    // game_time_elapsed = cumulative seconds since game-start, recorded
    // per decision so the value grows monotonically across rows.
    const gameTimeElapsedSeconds = gameStartRef.current
      ? Math.max(1, Math.round((nowMs - gameStartRef.current) / 1000))
      : timeElapsedSeconds;
    const scorePoints = isCorrect ? calculateScore({ timeElapsedSeconds, totalTries: currentTries }) : 0;

    const timestamp = new Date().toISOString();

    // Store decision for batch POST
    const newDecision = {
      game: gameId,
      game_session: currentGameSessionId,
      question: item.id,
      selected_option: column.id,
      time_elapsed: timeElapsedSeconds,
      game_time_elapsed: gameTimeElapsedSeconds,
      timestamp,
      is_correct: isCorrect,
      score_points: scorePoints,
      score_accuracy: isCorrect ? 1 : 0,
      total_tries: currentTries
    };
    // Append every drop — including wrong attempts that the player
    // later retries — so the bulk POST sends the full attempt history
    // to the backend (matches the multistep pattern). The pill is
    // safe because its memo iterates allDecisions and uses Map.set
    // per question for is_correct rows only, so multiple wrong rows
    // get skipped and the latest correct row's score wins.
    const nextAllDecisions = [...allDecisions, newDecision];
    setAllDecisions(nextAllDecisions);

    const result = { is_correct: isCorrect, score_points: scorePoints };
    callback(null, result);

    const footerMessageMiddle = isCorrect ? ' belongs in ' : ' does NOT belong in ';
    const footerMessage = <p><b>{item.name}</b>{footerMessageMiddle}<b>{column.name}</b>.</p>;

    if (isCorrect) {
      answerCorrectEffect.current.play().catch(e => console.log('Error playing answer correct sound:', e));

      setShouldScorePlaySound(true);
      showFooterMessage('success', footerMessage);

      setAvailableBlockItems(prev => {
        if (prev.length === 0) {
          setIsLevelCompleted(true);
        }
        return prev;
      });
    } else {
      answerWrongEffect.current.play().catch(e => console.log('Error playing answer wrong sound:', e));

      showFooterMessage('error', footerMessage);
      const { id, name, order, parents, description, startTime } = item;
      setAvailableBlockItems(prevAvalableBlockItems => [
        ...prevAvalableBlockItems,
        { id, accountItemId: item.accountItemId, name, order, parents, description, startTime, accuracyCoefficient: 0.5 }
      ]);
    }

    // Save to localStorage. Mirror the in-memory array so a refresh
    // mid-level rehydrates to the same multi-row-per-question shape;
    // the pill memo handles dedup at read time via Map.set on
    // is_correct rows.
    saveSessionToStorage(currentGameSessionId, {
      gameId,
      levelNumber,
      questionTries: { ...questionTries, [item.id]: currentTries },
      allDecisions: nextAllDecisions,
    });
  }
  
  // Wait for the completion POST (if still in flight), then retry once if
  // localStorage still holds decisions — i.e. the completion POST didn't succeed.
  const ensureDecisionsPosted = async () => {
    if (postInFlightRef.current) {
      await postInFlightRef.current;
    }
    const savedSession = loadSessionFromStorage(currentGameSessionId);
    if (savedSession?.allDecisions?.length) {
      try {
        const formattedDecisions = formatDecisionsForBulkPost(savedSession.allDecisions, time);
        await postDecisionsBulk(formattedDecisions);
        // Mark as POSTed BEFORE clearing storage. If the clear fails for
        // any reason, the marker still tells the rehydration to ignore
        // localStorage so the pill stays consistent with the backend.
        sessionStorage.setItem(`postCompleted-${currentGameSessionId}-level${levelNumber}`, 'true');
        clearSessionFromStorage(currentGameSessionId);
        // Refetch decisions so the derived pill reflects the backend's
        // recomputed score_points.
        queryClient.invalidateQueries({ queryKey: ['decisionsResponse'] });
      } catch (error) {
        console.error('Retry post decisions failed:', error);
      }
    }
  };

  const handleViewSummaryClick = async () => {
    await ensureDecisionsPosted();

    const navigateToSummary = () => {
      if (isOnboardingDemo) {
        // Skip points and badge — go directly to next level or summary
        if (scenarioLevels.length === levelNumber) {
          navigate(`/classification/summary`);
        } else {
          navigate(`/classification/level/${levelNumber + 1}/`);
        }
      } else {
        navigate(`/classification/level/${levelNumber}/points/`);
      }
    };

    // Per-level reflection tour. Runs whenever the button reads
    // "View Summary"; we suppress it only on the in-tournament
    // intermediate-level path (where the button reads "Next Level" and
    // the student is mid-competition). Instructor-onboarding levels now
    // show "View Summary" on every step so the tour fires on each.
    const isViewSummaryButton = !(isGameBelongsToTournament && scenarioLevels.length !== levelNumber);
    const reflectionTour = REFLECTION_TOURS_BY_LEVEL[scenarioLevel.name];
    if (reflectionTour && isViewSummaryButton) {
      setIsModalShown(false);
      startTour(reflectionTour, { onComplete: navigateToSummary });
      return;
    }

    navigateToSummary();
  };

  // Tournament completion: retry any pending decisions post and close the tab
  // (these tabs are opened via window.open, so window.close is allowed).
  const handleCloseTabClick = async () => {
    await ensureDecisionsPosted();
    window.close();
  };

  // Single-fire guard: once the end-of-game sequence kicks off, don't
  // let it re-trigger if some downstream effect (rehydration, refetch,
  // etc.) flips isPlaying back to true. Prevents duplicate mutate +
  // bulk POST calls.
  const endOfGameTriggeredRef = useRef(false);

  useEffect(() => {
    if (endOfGameTriggeredRef.current) return;
    if (isLevelCompleted && fallingBlocks.length === 0 && isPlaying) {
      const showWellDoneTimeout = setTimeout(() => {
        // Flip the guard inside the timeout — setting it before would
        // let the cleanup cancel this very timeout (because the effect
        // re-runs as deps change within the 700ms window) while the
        // next run early-returns. The Well Done modal AND the bulk
        // POST are both gated on this callback running.
        endOfGameTriggeredRef.current = true;
        updateGameSessionMutation.mutate({
          id: currentGameSessionId,
          levelNumber,
          levelStatus: 'completed',
          isSessionCompleted: scenarioLevels.length === levelNumber,
        });

        postInFlightRef.current = (async () => {
          try {
            const savedSession = loadSessionFromStorage(currentGameSessionId);
            const decisionsToPost = savedSession?.allDecisions || allDecisions;
            if (decisionsToPost.length) {
              const formattedDecisions = formatDecisionsForBulkPost(decisionsToPost, time);
              await postDecisionsBulk(formattedDecisions);
              // Mark as POSTed BEFORE clearing storage. If the clear
              // fails (tab closed, JS error, etc.), the marker still
              // tells the rehydration to treat localStorage as empty so
              // the pill stays consistent with the backend.
              sessionStorage.setItem(`postCompleted-${currentGameSessionId}-level${levelNumber}`, 'true');
              clearSessionFromStorage(currentGameSessionId);
              // Refetch decisions so the derived pill reflects the
              // backend's recomputed score_points.
              queryClient.invalidateQueries({ queryKey: ['decisionsResponse'] });
            }
          } catch (error) {
            console.error('Failed to post decisions:', error);
          }
        })();

        setIsPlaying(false);
        setIsModalOverlayVisible(false);
        setIsModalSmall(true);
        setModalContent(
          <>
            <div className="modal-center vertical">
              <div><span>Well done!</span></div>
              {
                isGameBelongsToTournament && scenarioLevels.length === levelNumber
                  ? <button onClick={handleCloseTabClick} className="button-blue button-view-summary">Close</button>
                  : <button onClick={handleViewSummaryClick} className="button-blue button-view-summary">
                      {isGameBelongsToTournament && scenarioLevels.length !== levelNumber ? 'Next Level' : 'View Summary'}
                    </button>
              }
            </div>
          </>
        );
        setIsModalShown(true);
      }, 700);

      return () => clearTimeout(showWellDoneTimeout);
    }
  }, [isLevelCompleted, fallingBlocks, levelNumber, totalScore, isPlaying, navigate, isGameBelongsToTournament]);

  const [showIntro, setShowIntro] = useState(true);

  const handleIntroClose = () => {
    setShowIntro(false);
  
    let countdown = 3;
    let countdownInterval;
  
    const updateModalContent = () => {
      if (countdown === 0) {
        setIsModalShown(false);
        setIsPlaying(true);
        clearInterval(countdownInterval);
  
        gameProcessEffect.current.volume = 0.09;
        gameProcessEffect.current.loop = true;
        if (soundStatus === 'enabled') {
          gameProcessEffect.current.play().catch(e => console.log('Error playing game process sound:', e));
        }
      } else {
        setModalContent(
          <div className="modal-center vertical">
            <div><span>Game start!</span></div>
            <div><span>{countdown}</span></div>
          </div>
        );
        countdown--;
      }
    };
    
    if (availableBlockItemsRef.current.length && !hasShownIntroModal) {
      setIsModalShown(true);
      setHasShownIntroModal(true);
  
      countdownInterval = setInterval(updateModalContent, 1000);
  
      const playSoundTimeout = setTimeout(() => {
        if (soundStatus === 'enabled') {
          countdownEffect.current.play().catch(e => console.log('Error playing countdown sound:', e));
        }
      }, 1000);
  
      return () => {
        clearTimeout(playSoundTimeout);
        clearInterval(countdownInterval);
      };
    } else {
      return () => {
        if (countdownInterval) {
          clearInterval(countdownInterval);
        }
      };
    }
  }

  useEffect(() => {  
    if (isPlaying && soundStatus === 'enabled') {
      gameProcessEffect.current.volume = 0.09;
      gameProcessEffect.current.loop = true;
      gameProcessEffect.current.play().catch(e => console.log('Error playing game process sound:', e));
    } else {
      gameProcessEffect.current.pause();
    }
  
    return () => {
      gameProcessEffect.current.pause();
    };
  }, [isPlaying, soundStatus]);

  useEffect(() => {
    const playClickSound = () => {
      if (soundStatus === 'enabled') {
        clickEffect.current.play().catch(e => console.error('Error playing click sound:', e));
      }
    };

    document.addEventListener('click', playClickSound);

    return () => {
      document.removeEventListener('click', playClickSound);
    };
  }, [soundStatus]);

  return (
    <div className="game-level-wrapper">
      {
        showIntro && (
          <Intro
            leftTitle={leftIntroTitle}
            leftSubtitle={leftIntroSubtitle}
            leftDescription={leftIntroDescription}
            rightTitle={rightIntroTitle}
            rightSubtitle={rightIntroSubtitle}
            rightDescription={rightIntroDescription}
            onShowingCompleted={handleIntroClose}
            isContentCentered={isIntroContentCentered}
            isSubtitleContentReversed={isIntroSubtitleContentReversed}
          />
      )}
      <Header 
        time={time}
        totalScore={totalScore}
        shouldScorePlaySound={shouldScorePlaySound}
      />
      <section className="main-section">
        <div className="container columns">
          <Column
            side='left'
            options={options[0]}
            levelNumber={levelNumber}
            handleDrop={handleDrop}
            prePlacedItems={prePlacedItems}
            gameType="Account Classification"
            tourId={REFLECTION_TOURS_BY_LEVEL[scenarioLevel.name] ? 'balance-sheet-column' : undefined}
          />
          <main className="content">
            <div className="falling-area-container">
              <div className="falling-area" ref={fallingAreaRef}>
                {fallingBlocks.map((block) => (
                  <FallingBlock
                    key={block.id}
                    id={block.id}
                    accountItemId={block.accountItemId}
                    name={block.name}
                    order={block.order}
                    parents={block.parents}
                    description={block.description}
                    startTime={block.startTime}
                    accuracyCoefficient={block.accuracyCoefficient}
                    left={block.left}
                    right={block.right}
                    y={block.y}  
                    onMouseEnter={() => handleMouseEnter(block.id, block.description)}
                    onMouseLeave={handleMouseLeave}
                    onMouseDown={handleMouseDown}
                    {...(isHintShown && hoveredItem === block.id && {
                      additionalContent: (
                        <Hint
                          content={hintContent}
                          side={block.left !== 'auto' ? 'left' : 'right'}
                        />
                      )
                    })}
                  />
                ))}
              </div>
            </div>
          </main>
          <Column
            side='right'
            options={options[1]}
            levelNumber={levelNumber}
            handleDrop={handleDrop}
            prePlacedItems={prePlacedItems}
            gameType="Account Classification"
            tourId={REFLECTION_TOURS_BY_LEVEL[scenarioLevel.name] ? 'income-statement-column' : undefined}
          />
        </div>
      </section>
      <footer className="game-footer">
        {isFooterMessageShown && (
          <FooterMessage
            type={footerMessageType}
            description={footerMessageDescription}
          />
        )}
      </footer>
      {isModalShown && (
        <Modal
          content={modalContent}
          isModalSmall={isModalSmall}
        />
      )}
    </div>
  );
}
